'use client';

import { Box, Divider } from '@wishket/design-system';
import { useRef, useState } from 'react';
import { useClickOutside } from '@wishket/yogokit';
import { usePathname, useRouter } from 'next/navigation';

import { useArticleLike } from './ArticleDetailMain.parts.hooks';
import { getShareParams, openNavigatorShare } from './ArticleDetailMain.utils';

import {
  useArticleActionStatus,
  useGetIsScrappedByArticleId,
} from '@/entities/article';
import { ScrapBox, ShareBox } from '@/shared/Components';
import { useAuth, useDeviceCheck } from '@/shared/hooks';
import { PATH } from '@/shared/constants';
import { ArticleRowToolbar } from '@/features/ArticleDetail';

interface ArticleDetailBottomToolbarProps {
  id: string;
  handleCommentView: () => void;
  articleTitle: string;
}

const ArticleDetailBottomToolbar = ({
  id,
  handleCommentView,
  articleTitle,
}: ArticleDetailBottomToolbarProps) => {
  const articleId = Number(id);
  const { isDesktop } = useDeviceCheck();
  const { like, isLiked, comment } = useArticleActionStatus(articleId);
  const isScrapped = useGetIsScrappedByArticleId(articleId);
  const { handleLikeAction } = useArticleLike(id);
  const { shareTitle, shareUrl } = getShareParams(articleTitle, id);
  const pathname = usePathname();
  const { isAuth } = useAuth();
  const router = useRouter();

  const [isShareBoxOpen, setIsShareBoxOpen] = useState(false);
  const [isScrapBoxOpen, setIsScrapBoxOpen] = useState(false);
  const scrapBoxRef = useRef<HTMLDivElement>(null);
  const shareBoxRef = useRef<HTMLDivElement>(null);

  const openShareBox = () => {
    if (isScrapBoxOpen) {
      closeScrapBox();
    }
    setIsShareBoxOpen(true);
  };

  const closeShareBox = () => {
    setIsShareBoxOpen(false);
  };

  const openScrapBox = () => {
    if (!isAuth) {
      router.push(`${PATH.AUTH_LOGIN}?next=${pathname}`);
      return;
    }
    if (isShareBoxOpen) {
      closeShareBox();
    }
    setIsScrapBoxOpen(true);
  };

  const closeScrapBox = () => {
    setIsScrapBoxOpen(false);
  };

  useClickOutside(shareBoxRef, closeShareBox);
  useClickOutside(scrapBoxRef, closeScrapBox);

  if (!like) return;
  if (!comment) return;

  return (
    <Box className="relative flex flex-col gap-4">
      <ArticleRowToolbar
        like={like}
        isLiked={isLiked}
        comment={comment}
        isScrapped={isScrapped}
        onLikeClick={handleLikeAction}
        onCommentClick={handleCommentView}
        onScrapClick={openScrapBox}
        onShareClick={() => {
          isDesktop ? openShareBox() : openNavigatorShare(shareTitle, shareUrl);
        }}
      />
      <Divider />
      {/* TODO: 툴바 ShareBox, ScrapBox 관련 useState, useRef, open, close 기능 커스텀 훅으로 분리 */}
      {isShareBoxOpen && (
        <Box
          className="absolute right-0 top-[58px] w-[240px]"
          ref={shareBoxRef}
        >
          <ShareBox url={shareUrl} title={shareTitle} />
        </Box>
      )}
      <ScrapBox
        className="absolute right-10 top-[58px] z-30 w-[240px]"
        scrapBoxRef={scrapBoxRef}
        articleId={articleId}
        isOpen={isScrapBoxOpen}
        onClose={closeScrapBox}
      />
    </Box>
  );
};

export default ArticleDetailBottomToolbar;
